import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Lightbulb, Users, Sprout, Rocket, Handshake, CreditCard, Shield, GraduationCap, Brain, Star, Award, Info, Send, Mail, CheckCircle, User2, Ticket, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import NominationModal from '../components/NominationModal';

interface Category {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  criteria: string[];
  sponsor?: {
    name: string;
    logo: string;
  };
}

const categories: Category[] = [
  {
    id: 1,
    title: "Innovation in Digital Banking",
    description: "Recognizing groundbreaking solutions that are transforming traditional banking services through digital innovation.",
    icon: <Lightbulb className="h-8 w-8" />,
    criteria: [
      "Technical innovation and uniqueness",
      "Customer experience and usability",
      "Market impact and adoption",
      "Security and reliability"
    ],
    sponsor: {
      name: "Liquid C2",
      logo: "https://liquidc2.com/wp-content/uploads/sites/3//2022/11/C2-H-AH.svg"
    }
  },
  {
    id: 2,
    title: "Fighting For Financial Inclusion",
    description: "Celebrating initiatives that bring financial services to underserved communities and bridge the financial divide.",
    icon: <Users className="h-8 w-8" />,
    criteria: [
      "Reach and accessibility",
      "Social impact measurement",
      "Sustainability of solution",
      "Cost-effectiveness"
    ],
    sponsor: {
      name: "Tether",
      logo: "https://tether.to/images/logoGreen.png"
    }
  },
  {
    id: 3,
    title: "Green Finance Award",
    description: "Honoring financial solutions that promote environmental sustainability and combat climate change.",
    icon: <Sprout className="h-8 w-8" />,
    criteria: [
      "Environmental impact",
      "Innovation in sustainable finance",
      "Measurable outcomes",
      "Scalability"
    ]
  },
  {
    id: 4,
    title: "Best Fintech Start Up",
    description: "Recognizing promising new companies that demonstrate exceptional potential in financial technology.",
    icon: <Rocket className="h-8 w-8" />,
    criteria: [
      "Innovation and uniqueness",
      "Growth potential",
      "Business model viability",
      "Team capability"
    ]
  },
  {
    id: 5,
    title: "Cryptocurrency Victor",
    description: "Acknowledging excellence in cryptocurrency and blockchain-based financial solutions.",
    icon: <Trophy className="h-8 w-8" />,
    criteria: [
      "Technical innovation",
      "Market adoption",
      "Security measures",
      "Regulatory compliance"
    ],
  },
  {
    id: 6,
    title: "The Collaboration of 2024",
    description: "Celebrating successful partnerships between traditional financial institutions and fintech innovators.",
    icon: <Handshake className="h-8 w-8" />,
    criteria: [
      "Partnership impact",
      "Innovation level",
      "Market success",
      "Integration effectiveness"
    ]
  },
  {
    id: 7,
    title: "Excellence in Payments",
    description: "Recognizing innovative payment solutions that are reshaping how we transfer and manage money.",
    icon: <CreditCard className="h-8 w-8" />,
    criteria: [
      "Innovation in payment technology",
      "User experience",
      "Security features",
      "Market adoption"
    ]
  },
  {
    id: 8,
    title: "Best Regtech Award",
    description: "Honoring solutions that effectively address regulatory compliance challenges in financial services.",
    icon: <Shield className="h-8 w-8" />,
    criteria: [
      "Compliance effectiveness",
      "Innovation in regulation",
      "Ease of implementation",
      "Cost efficiency"
    ]
  },
  {
    id: 9,
    title: "Financial Literacy Champion",
    description: "Recognizing initiatives that excel in promoting financial education and awareness.",
    icon: <GraduationCap className="h-8 w-8" />,
    criteria: [
      "Educational impact",
      "Reach and accessibility",
      "Content quality",
      "Innovation in delivery"
    ]
  },
  {
    id: 10,
    title: "Thought Leader of the Year",
    description: "Celebrating individuals who have made significant contributions to fintech thought leadership.",
    icon: <Brain className="h-8 w-8" />,
    criteria: [
      "Industry influence",
      "Innovation advocacy",
      "Knowledge sharing",
      "Vision and strategy"
    ]
  },
  {
    id: 11,
    title: "Fintech Entrepreneur of 2024",
    description: "Recognizing outstanding entrepreneurial achievement in financial technology.",
    icon: <Star className="h-8 w-8" />,
    criteria: [
      "Business success",
      "Innovation leadership",
      "Market impact",
      "Future vision"
    ],
    sponsor: {
      name: "Everest Wealth",
      logo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT9h2uRb-kCePLLaP6_X1fdGf3q9MYfpgDweg&s"
    }
  },
  {
    id: 12,
    title: "Lifetime Achievement Award",
    description: "Honoring individuals who have made lasting contributions to the development of fintech in South Africa.",
    icon: <Award className="h-8 w-8" />,
    criteria: [
      "Career impact",
      "Industry contribution",
      "Leadership legacy",
      "Innovation history"
    ]
  }
];

const Categories = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleNominate = (category: string) => {
    setSelectedCategory(category);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section with Parallax */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <motion.div
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://titc.io/wp-content/uploads/2024/12/Screenshot-2024-10-18-at-08.48.34.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70" />
        </motion.div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white mb-6">
              Celebrating
              <span className="block" style={{ color: '#C1B3A3' }}>
                Excellence
              </span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-200 mb-8">
              Twelve Prestigious Categories Recognizing Innovation in FinTech
            </p>
            <Link
              to="/tickets"
              className="inline-flex items-center px-8 py-4 rounded-lg text-white font-semibold transition-all duration-300 hover:scale-105 space-x-2"
              style={{ backgroundColor: '#C1B3A3' }}
            >
              <Ticket className="h-5 w-5" />
              <span>Reserve Your Ticket</span>
            </Link>
          </motion.div>
        </div>
      </section>

      <div className="bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="text-4xl font-bold mb-4" style={{ color: '#1A1A1A' }}>Award Categories</h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              Celebrating excellence across twelve prestigious categories in South African financial technology
            </p>
            
            {/* Nominations Closed Banner */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-red-50 border border-red-200 rounded-xl p-6 mb-12 max-w-4xl mx-auto"
            >
              <div className="flex items-center justify-center space-x-3">
                <AlertTriangle className="h-8 w-8 text-red-600" />
                <div>
                  <h2 className="text-2xl font-bold text-red-800 mb-2">Nominations Closed</h2>
                  <p className="text-red-700">
                    The nomination period for the SA FinTech Awards 2025 has ended. Thank you to everyone who submitted nominations. 
                    Winners will be announced at the awards ceremony on November 25th, 2025.
                  </p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-lg p-6 mb-12 max-w-4xl mx-auto"
              style={{ backgroundColor: 'rgba(193, 179, 163, 0.1)' }}
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 mt-1">
                  <Info className="h-6 w-6" style={{ color: '#C1B3A3' }} />
                </div>
                <div className="text-left">
                  <h2 className="text-xl font-semibold mb-3" style={{ color: '#C1B3A3' }}>Judging Process Information</h2>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <span className="w-2 h-2 mt-2 mr-2 rounded-full" style={{ backgroundColor: '#C1B3A3' }}></span>
                      <span>All nominations are being evaluated by our expert judging panel based on the specific category criteria.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 mt-2 mr-2 rounded-full" style={{ backgroundColor: '#C1B3A3' }}></span>
                      <span>Each nomination is assessed independently for innovation, market impact, and excellence in their respective categories.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 mt-2 mr-2 rounded-full" style={{ backgroundColor: '#C1B3A3' }}></span>
                      <span>The judging process maintains the highest standards of integrity and transparency throughout.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 mt-2 mr-2 rounded-full" style={{ backgroundColor: '#C1B3A3' }}></span>
                      <span>Winners will be announced exclusively at the gala dinner ceremony on November 25th, 2025.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div style={{ color: '#C1B3A3' }}>
                      {category.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{category.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-4">{category.description}</p>
                  {category.sponsor && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500 mb-2">Sponsored by</p>
                      <div className="h-12">
                        <img
                          src={category.sponsor.logo}
                          alt={`${category.sponsor.name} logo`}
                          className="h-full object-contain mx-auto"
                        />
                      </div>
                    </div>
                  )}
                  <div className="border-t pt-4" style={{ borderColor: 'rgba(193, 179, 163, 0.2)' }}>
                    <h4 className="font-semibold text-gray-900 mb-2">Judging Criteria:</h4>
                    <ul className="space-y-2 mb-6">
                      {category.criteria.map((criterion, idx) => (
                        <li key={idx} className="text-gray-600 text-sm flex items-center">
                          <span className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: '#C1B3A3' }}></span>
                          {criterion}
                        </li>
                      ))}
                    </ul>
                    {/* Nomination button removed - nominations closed */}
                    <div className="text-center py-3 px-4 bg-gray-100 rounded-md">
                      <span className="text-gray-500 text-sm font-medium">Nominations Closed</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold mb-4" style={{ color: '#C1B3A3' }}>How the Judging Process Works</h2>
              <div className="h-1 w-24 mx-auto rounded-full" style={{ backgroundColor: '#C1B3A3' }}></div>
            </motion.div>

            <div className="space-y-6">
              {[
                {
                  icon: <Send className="h-8 w-8" />,
                  title: "Nominations Received",
                  content: "All nominations have been collected and are now being processed by our expert judging panel."
                },
                {
                  icon: <Mail className="h-8 w-8" />,
                  title: "Nominee Verification",
                  content: "Our team is contacting all nominees to verify information and gather additional details for the judging process."
                },
                {
                  icon: <CheckCircle className="h-8 w-8" />,
                  title: "Expert Evaluation",
                  content: "Each nomination is being carefully evaluated by our panel of industry experts based on the specific category criteria."
                },
                {
                  icon: <User2 className="h-8 w-8" />,
                  title: "Independent Review",
                  content: "A comprehensive review process ensures fair and impartial assessment of all nominations."
                },
                {
                  icon: <Trophy className="h-8 w-8" />,
                  title: "Winners Announcement",
                  content: "Winners will be announced at the prestigious SA FinTech Awards ceremony on November 25th, 2025."
                }
              ].map((step, index) => (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start space-x-6 bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full"
                         style={{ backgroundColor: 'rgba(193, 179, 163, 0.2)' }}>
                      <div style={{ color: '#C1B3A3' }}>{step.icon}</div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                    <p className="text-gray-600">{step.content}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {selectedCategory && (
        <NominationModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedCategory(null);
          }}
          category={selectedCategory}
        />
      )}
    </div>
  );
};

export default Categories;